import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar as CalendarIcon, Clock, MapPin, User, ArrowRight, ArrowLeft, Sparkles } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { cn } from "../lib/utils";
import { DateInput, isValidDate, formatISO } from "./DateInput";

const STEPS = ["name", "dob", "tob", "pob"];

export const PredictionForm = ({ onSubmit, onBack }) => {
  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [gender, setGender] = useState("");
  const [dob, setDob] = useState({ d: "", m: "", y: "" });
  const [tob, setTob] = useState("");
  const [pob, setPob] = useState("");

  const canContinue = () => {
    if (step === 0) return name.trim().length >= 2;
    if (step === 1) return isValidDate(dob);
    if (step === 2) return tob === "" || /^\d{2}:\d{2}$/.test(tob);
    if (step === 3) return pob.trim().length >= 2;
    return false;
  };

  const next = () => {
    if (!canContinue()) return;
    if (step < STEPS.length - 1) setStep(step + 1);
    else {
      onSubmit({
        name: name.trim(),
        gender: gender || null,
        date_of_birth: formatISO(dob),
        time_of_birth: tob || null,
        place_of_birth: pob.trim(),
      });
    }
  };

  const prev = () => {
    if (step === 0) onBack?.();
    else setStep(step - 1);
  };

  const skipTime = () => {
    setTob("");
    setStep(step + 1);
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center px-4 sm:px-6 py-20 sm:py-24">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="glass rounded-2xl p-6 sm:p-10 shadow-[0_8px_40px_rgba(0,0,0,0.5)]">
          {/* Progress */}
          <div className="flex items-center gap-2 mb-6 sm:mb-8" data-testid="form-progress">
            {STEPS.map((_, i) => (
              <div
                key={i}
                className={cn(
                  "h-[2px] flex-1 rounded-full transition-all duration-500",
                  i <= step ? "bg-stardust" : "bg-white/10"
                )}
              />
            ))}
          </div>

          <div className="mb-6 sm:mb-8">
            <p className="text-stardust/80 uppercase tracking-[0.3em] text-[10px] mb-3">
              Step {step + 1} of {STEPS.length}
            </p>
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl text-[#F5EBD8] leading-tight">
              {step === 0 && "What name do the stars call you?"}
              {step === 1 && "When did you arrive?"}
              {step === 2 && "At which hour?"}
              {step === 3 && "Where did the sky greet you?"}
            </h2>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-4"
            >
              {step === 0 && (
                <div>
                  <Label className="text-slate-400 text-xs uppercase tracking-[0.2em] flex items-center gap-2 mb-3">
                    <User className="h-3 w-3 text-stardust" /> Full name
                  </Label>
                  <Input
                    data-testid="form-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Aria Solene"
                    className="bg-black/30 border-white/10 text-white placeholder:text-white/30 h-12 focus-visible:ring-stardust focus-visible:border-stardust text-base"
                    autoFocus
                    onKeyDown={(e) => e.key === "Enter" && next()}
                  />
                  <div className="mt-5">
                    <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">
                      How should we refer to you? <span className="text-stardust/60 normal-case tracking-normal">(optional)</span>
                    </p>
                    <div className="flex gap-2 flex-wrap" data-testid="form-gender-chips">
                      {[
                        { v: "woman",       l: "Woman" },
                        { v: "man",         l: "Man" },
                        { v: "non-binary",  l: "Non-binary" },
                        { v: "",            l: "Prefer not to say" },
                      ].map((g) => (
                        <button
                          key={g.l}
                          type="button"
                          onClick={() => setGender(g.v)}
                          data-testid={`form-gender-${g.v || "skip"}`}
                          className={cn(
                            "text-xs px-3 py-1.5 rounded-full border transition-all",
                            gender === g.v
                              ? "bg-stardust/15 border-stardust text-stardust"
                              : "bg-white/[0.02] border-white/10 text-slate-300 hover:border-stardust/50 hover:text-stardust"
                          )}
                        >
                          {g.l}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {step === 1 && (
                <div>
                  <Label className="text-slate-400 text-xs uppercase tracking-[0.2em] flex items-center gap-2 mb-4">
                    <CalendarIcon className="h-3 w-3 text-stardust" /> Date of birth
                  </Label>
                  <DateInput value={dob} onChange={setDob} autoFocus />
                  <p className="text-white/40 text-xs mt-4 text-center">
                    Type the day, month and year. Fields auto-advance.
                  </p>
                </div>
              )}

              {step === 2 && (
                <div>
                  <Label className="text-slate-400 text-xs uppercase tracking-[0.2em] flex items-center gap-2 mb-3">
                    <Clock className="h-3 w-3 text-stardust" /> Time of birth
                  </Label>
                  <Input
                    data-testid="form-tob-input"
                    type="time"
                    value={tob}
                    onChange={(e) => setTob(e.target.value)}
                    className="bg-black/30 border-white/10 text-white h-14 text-base focus-visible:ring-stardust focus-visible:border-stardust [color-scheme:dark]"
                  />
                  <p className="text-white/50 text-xs mt-3 italic leading-relaxed">
                    Optional, but recommended for a sharper rising sign reading.
                    Don&apos;t know it? You can{" "}
                    <button
                      type="button"
                      onClick={skipTime}
                      data-testid="form-tob-skip"
                      className="text-stardust underline underline-offset-2 hover:text-stardust-light"
                    >
                      skip this step
                    </button>
                    .
                  </p>
                </div>
              )}

              {step === 3 && (
                <div>
                  <Label className="text-slate-400 text-xs uppercase tracking-[0.2em] flex items-center gap-2 mb-3">
                    <MapPin className="h-3 w-3 text-stardust" /> Place of birth
                  </Label>
                  <Input
                    data-testid="form-pob-input"
                    value={pob}
                    onChange={(e) => setPob(e.target.value)}
                    placeholder="City, Country"
                    className="bg-black/30 border-white/10 text-white placeholder:text-white/30 h-12 focus-visible:ring-stardust focus-visible:border-stardust text-base"
                    onKeyDown={(e) => e.key === "Enter" && next()}
                    autoFocus
                  />
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          <div className="flex items-center justify-between mt-8 sm:mt-10 gap-3">
            <Button
              data-testid="form-back-button"
              variant="ghost"
              onClick={prev}
              className="text-slate-400 hover:text-white hover:bg-white/5 px-3 sm:px-4"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <Button
              data-testid="form-next-button"
              onClick={next}
              disabled={!canContinue()}
              className="bg-stardust text-cosmic-900 hover:bg-stardust-light hover:shadow-[0_0_24px_rgba(226,194,117,0.4)] disabled:opacity-40 disabled:hover:shadow-none rounded-full px-5 sm:px-6 py-5 font-medium transition-all"
            >
              {step === STEPS.length - 1 ? (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Read my sky
                </>
              ) : (
                <>
                  Continue
                  <ArrowRight className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </div>
      </motion.div>
    </section>
  );
};
